"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useTheme } from "../../Utils/useTheme";

export default function HeroGlobe() {
  const containerRef = useRef(null);
  const { theme } = useTheme();

  const isGreen = theme === "green";
  const isLight = theme === "light";

  const orangeColor = isGreen ? 0x10b981 : (isLight ? 0xea580c : 0xf97316);
  const amberColor = isGreen ? 0x059669 : (isLight ? 0xd97706 : 0xf59e0b);
  const goldColor = isGreen ? 0x34d399 : (isLight ? 0xca8a04 : 0xfbbf24);

  // Draw simplified continent polygons on an offscreen canvas
  const drawWorldMap = (ctx, width, height) => {
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, width, height);

    const continents = [
      // North America (More detailed coastlines)
      [
        [0.02, 0.16], [0.08, 0.12], [0.15, 0.10], [0.25, 0.10], [0.28, 0.14],
        [0.33, 0.14], [0.35, 0.20], [0.33, 0.28], [0.31, 0.32], [0.33, 0.36],
        [0.31, 0.40], [0.27, 0.42], [0.23, 0.44], [0.23, 0.48], [0.24, 0.52],
        [0.21, 0.55], [0.19, 0.54], [0.18, 0.50], [0.17, 0.44], [0.14, 0.42],
        [0.10, 0.38], [0.08, 0.30], [0.06, 0.25], [0.02, 0.22]
      ],
      // South America (More detailed coastlines)
      [
        [0.23, 0.53], [0.28, 0.52], [0.31, 0.54], [0.35, 0.58], [0.38, 0.63],
        [0.36, 0.70], [0.34, 0.76], [0.30, 0.84], [0.26, 0.90], [0.24, 0.90],
        [0.23, 0.85], [0.22, 0.75], [0.21, 0.67], [0.21, 0.60], [0.22, 0.55]
      ],
      // Greenland
      [
        [0.27, 0.07], [0.33, 0.06], [0.37, 0.08], [0.38, 0.12], [0.34, 0.16],
        [0.30, 0.16], [0.28, 0.12]
      ],
      // Eurasia (Detailed Europe + Asia including India, Middle East, Southeast Asia)
      [
        [0.37, 0.22], [0.40, 0.16], [0.43, 0.14], [0.45, 0.16], [0.44, 0.22], // Scandinavia
        [0.48, 0.18], [0.52, 0.15], [0.65, 0.12], [0.80, 0.13], [0.85, 0.15], // Siberia
        [0.88, 0.22], [0.89, 0.30], [0.87, 0.38], [0.85, 0.44], [0.81, 0.48], // East Asia / China
        [0.78, 0.54], [0.75, 0.55], [0.73, 0.51], [0.70, 0.49], // Southeast Asia
        [0.66, 0.49], [0.64, 0.54], [0.61, 0.53], [0.62, 0.47], // India
        [0.58, 0.47], [0.55, 0.45], [0.53, 0.48], [0.49, 0.48], // Arabian Peninsula / Persian Gulf
        [0.47, 0.44], [0.46, 0.41], [0.44, 0.42], [0.42, 0.38], // Italy / Balkans
        [0.39, 0.36], [0.37, 0.35], [0.35, 0.30], [0.36, 0.25]  // Iberia
      ],
      // Africa (More detailed coastlines)
      [
        [0.43, 0.46], [0.48, 0.44], [0.52, 0.44], [0.55, 0.46], [0.59, 0.50],
        [0.61, 0.53], [0.63, 0.57], [0.61, 0.64], [0.59, 0.70], [0.56, 0.75],
        [0.53, 0.81], [0.50, 0.83], [0.48, 0.80], [0.47, 0.74], [0.46, 0.66],
        [0.44, 0.62], [0.42, 0.58], [0.39, 0.54], [0.40, 0.50]
      ],
      // Australia
      [
        [0.77, 0.68], [0.80, 0.66], [0.85, 0.67], [0.88, 0.70], [0.87, 0.76],
        [0.82, 0.81], [0.79, 0.80], [0.76, 0.77], [0.76, 0.72]
      ],
      // United Kingdom / Ireland
      [
        [0.39, 0.25], [0.41, 0.24], [0.41, 0.28], [0.39, 0.28]
      ],
      // Japan
      [
        [0.85, 0.30], [0.87, 0.34], [0.86, 0.38], [0.84, 0.34]
      ],
      // Madagascar
      [
        [0.59, 0.72], [0.61, 0.72], [0.60, 0.78], [0.58, 0.78]
      ],
      // Indonesia / Philippines Islands
      [
        [0.70, 0.54], [0.74, 0.56], [0.76, 0.58], [0.72, 0.60]
      ],
      [
        [0.75, 0.61], [0.78, 0.62], [0.77, 0.65], [0.74, 0.64]
      ],
      // Antarctica
      [
        [0.05, 0.90], [0.25, 0.89], [0.50, 0.90], [0.75, 0.89], [0.95, 0.90],
        [0.95, 0.96], [0.05, 0.96]
      ]
    ];

    ctx.fillStyle = "#ffffff";
    continents.forEach((poly) => {
      ctx.beginPath();
      poly.forEach((pt, idx) => {
        const px = pt[0] * width;
        const py = pt[1] * height;
        if (idx === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.fill();
    });
  };

  // Custom Mesh Helpers
  const createAirplaneMesh = () => {
    const group = new THREE.Group();
    // Fuselage
    const bodyGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.12, 8);
    bodyGeo.rotateX(Math.PI / 2);
    const bodyMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.2, metalness: 0.1 });
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    group.add(body);

    // Wings
    const wingGeo = new THREE.BoxGeometry(0.15, 0.004, 0.024);
    const wingMat = new THREE.MeshStandardMaterial({ color: 0x00f2fe, emissive: 0x00f2fe, emissiveIntensity: 0.5 });
    const wings = new THREE.Mesh(wingGeo, wingMat);
    group.add(wings);

    // Tail
    const tailGeo = new THREE.BoxGeometry(0.004, 0.03, 0.016);
    tailGeo.translate(0, 0.015, -0.045);
    const tail = new THREE.Mesh(tailGeo, bodyMat);
    group.add(tail);

    return group;
  };

  const createShipMesh = () => {
    const group = new THREE.Group();
    // Hull
    const hullGeo = new THREE.BoxGeometry(0.035, 0.025, 0.1, 4, 2, 4);
    const hullMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.7 });
    const hull = new THREE.Mesh(hullGeo, hullMat);
    group.add(hull);

    // Deckhouse
    const cabinGeo = new THREE.BoxGeometry(0.03, 0.02, 0.024);
    cabinGeo.translate(0, 0.02, -0.03);
    const cabinMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc });
    const cabin = new THREE.Mesh(cabinGeo, cabinMat);
    group.add(cabin);

    // Cargo Containers (glowing boxes)
    const cargoColors = [0xf97316, 0x00f2fe, 0xfbbf24];
    for (let i = 0; i < 3; i++) {
      const color = cargoColors[i];
      const boxGeo = new THREE.BoxGeometry(0.026, 0.02, 0.018);
      boxGeo.translate(0, 0.018, 0.025 - i * 0.02);
      const boxMat = new THREE.MeshStandardMaterial({
        color: color,
        emissive: color,
        emissiveIntensity: 0.4,
      });
      const containerBox = new THREE.Mesh(boxGeo, boxMat);
      group.add(containerBox);
    }

    return group;
  };

  const createTruckMesh = () => {
    const group = new THREE.Group();
    // Cab
    const cabinGeo = new THREE.BoxGeometry(0.026, 0.026, 0.026);
    cabinGeo.translate(0, 0.01, 0.035);
    const cabinMat = new THREE.MeshStandardMaterial({ color: 0xf59e0b, roughness: 0.3 });
    const cabin = new THREE.Mesh(cabinGeo, cabinMat);
    group.add(cabin);

    // Trailer
    const trailerGeo = new THREE.BoxGeometry(0.026, 0.03, 0.06);
    trailerGeo.translate(0, 0.012, -0.01);
    const trailerMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 });
    const trailer = new THREE.Mesh(trailerGeo, trailerMat);
    group.add(trailer);

    return group;
  };

  const createTrainMesh = () => {
    const group = new THREE.Group();
    const segmentCount = 3;
    for (let i = 0; i < segmentCount; i++) {
      const segGeo = new THREE.BoxGeometry(0.016, 0.012, 0.035);
      segGeo.translate(0, 0.006, -i * 0.04);
      const segMat = new THREE.MeshStandardMaterial({
        color: i === 0 ? 0xf59e0b : 0xe2e8f0,
        roughness: 0.2,
        metalness: 0.8,
      });
      const segment = new THREE.Mesh(segGeo, segMat);
      group.add(segment);
    }
    return group;
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const width = rect.width || 500;
    const height = rect.height || 500;

    const getSceneFit = (w, h) => {
      const minSide = Math.max(Math.min(w, h), 320);
      const radius = THREE.MathUtils.clamp(minSide / 135, 2.7, 3.5);
      const cameraZ = THREE.MathUtils.clamp(radius * 3.8, 10.0, 13.0);
      return { radius, cameraZ };
    };
    let sceneFit = getSceneFit(width, height);
    const radius = sceneFit.radius;

    // Shared geometries and arrays for animations
    const pulseGeo = new THREE.RingGeometry(radius * 0.018, radius * 0.035, 16);
    const pGeo = new THREE.SphereGeometry(radius * 0.012, 8, 8);
    const pulsingRings = [];

    // 1. Scene Setup
    const scene = new THREE.Scene();

    // 2. Camera Setup
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, sceneFit.cameraZ);

    // 3. Renderer Setup
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(width, height, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.inset = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    container.appendChild(renderer.domElement);

    // 4. Orbit Controls (Auto Rotate)
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = false;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.35;
    controls.maxPolarAngle = Math.PI / 1.4;
    controls.minPolarAngle = Math.PI / 3;

    // 5. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.45);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(orangeColor, 2.5); // Sunset
    dirLight1.position.set(6, 4, 8);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0x00f2fe, 1.8); // Cyan Twilight
    dirLight2.position.set(-6, -4, -8);
    scene.add(dirLight2);

    const pointLight = new THREE.PointLight(goldColor, 2.5, 15); // Core
    pointLight.position.set(0, 0, 0);
    scene.add(pointLight);

    // 6. Globe Group
    const globeGroup = new THREE.Group();
    scene.add(globeGroup);

    // A. Inner Glow Sphere
    const innerSphereGeo = new THREE.SphereGeometry(radius * 0.96, 48, 48);
    const innerSphereMat = new THREE.MeshPhongMaterial({
      color: orangeColor,
      emissive: amberColor,
      emissiveIntensity: 0.15,
      transparent: true,
      opacity: 0.08,
      wireframe: true,
    });
    const innerSphere = new THREE.Mesh(innerSphereGeo, innerSphereMat);
    globeGroup.add(innerSphere);

    // B. Dot-Matrix EarthContinent Sampling
    const mapCanvas = document.createElement("canvas");
    mapCanvas.width = 512;
    mapCanvas.height = 256;
    const mapCtx = mapCanvas.getContext("2d");
    if (mapCtx) {
      drawWorldMap(mapCtx, mapCanvas.width, mapCanvas.height);
    }
    const mapData = mapCtx ? mapCtx.getImageData(0, 0, mapCanvas.width, mapCanvas.height) : null;

    const landPoints = [];
    const oceanPoints = [];

    const totalSamples = 14000;
    for (let i = 0; i < totalSamples; i++) {
      // Fibonacci sphere distribution
      const phi = Math.acos(1 - 2 * (i + 0.5) / totalSamples);
      const theta = Math.sqrt(totalSamples * Math.PI) * phi;

      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.cos(phi);
      const z = radius * Math.sin(phi) * Math.sin(theta);

      // Spherical coordinates -> UV texture mapping
      const u = 0.5 + Math.atan2(z, x) / (2 * Math.PI);
      const v = 0.5 - Math.asin(y / radius) / Math.PI;

      if (mapData) {
        const px = Math.floor(u * mapCanvas.width);
        const py = Math.floor(v * mapCanvas.height);
        const pixelIdx = (py * mapCanvas.width + px) * 4;
        const isLand = mapData.data[pixelIdx] > 128;

        if (isLand) {
          landPoints.push(x, y, z);
        } else {
          // Sparse points for oceans
          if (Math.random() < 0.18) {
            oceanPoints.push(x * 0.995, y * 0.995, z * 0.995);
          }
        }
      } else {
        landPoints.push(x, y, z);
      }
    }

    // Land Points
    const landPositions = new Float32Array(landPoints);
    const landGeo = new THREE.BufferGeometry();
    landGeo.setAttribute("position", new THREE.BufferAttribute(landPositions, 3));
    const landMat = new THREE.PointsMaterial({
      color: goldColor, // Gold dots
      size: 0.052,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
    });
    const landMesh = new THREE.Points(landGeo, landMat);
    globeGroup.add(landMesh);

    // Ocean Points
    const oceanPositions = new Float32Array(oceanPoints);
    const oceanGeo = new THREE.BufferGeometry();
    oceanGeo.setAttribute("position", new THREE.BufferAttribute(oceanPositions, 3));
    const oceanMat = new THREE.PointsMaterial({
      color: 0x1e3a8a, // Slate/Blue dots
      size: 0.026,
      transparent: true,
      opacity: 0.32,
      blending: THREE.AdditiveBlending,
    });
    const oceanMesh = new THREE.Points(oceanGeo, oceanMat);
    globeGroup.add(oceanMesh);

    // C. Coordinate Helpers
    const latLonToVector3 = (lat, lon, r) => {
      const phi = (lat * Math.PI) / 180;
      const theta = (lon * Math.PI) / 180;
      return new THREE.Vector3(
        r * Math.cos(phi) * Math.cos(theta),
        r * Math.sin(phi),
        r * Math.cos(phi) * Math.sin(theta)
      );
    };

    // Major Supply Chain Nodes (Cities)
    const cities = [
      { name: "New York", lat: 40.7128, lon: -74.0060, color: 0x00f2fe },
      { name: "London", lat: 51.5074, lon: -0.1278, color: 0x00f2fe },
      { name: "Dubai", lat: 25.2048, lon: 55.2708, color: 0xf59e0b },
      { name: "Singapore", lat: 1.3521, lon: 103.8198, color: 0xa855f7 },
      { name: "Shanghai", lat: 31.2304, lon: 121.4737, color: 0xf97316 },
    ];

    const cityNodes = [];
    cities.forEach((city) => {
      const pos = latLonToVector3(city.lat, city.lon, radius);
      cityNodes.push(pos);

      // Pins/Markers at node positions
      const pinGroup = new THREE.Group();

      const pinHeight = radius * 0.045;
      const pinGeo = new THREE.CylinderGeometry(radius * 0.003, radius * 0.009, pinHeight, 8);
      pinGeo.translate(0, pinHeight / 2, 0);
      pinGeo.rotateX(Math.PI / 2);
      const pinMat = new THREE.MeshPhongMaterial({
        color: city.color,
        emissive: city.color,
        emissiveIntensity: 0.9,
      });
      const pin = new THREE.Mesh(pinGeo, pinMat);
      pin.position.copy(pos);
      pin.lookAt(0, 0, 0);
      pin.rotateX(Math.PI / 2);
      pinGroup.add(pin);

      const innerRingRadius = radius * 0.018;
      const outerRingRadius = radius * 0.035;
      const ringGeo = new THREE.RingGeometry(innerRingRadius, outerRingRadius, 16);
      const ringMat = new THREE.MeshBasicMaterial({
        color: city.color,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.85,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.copy(pos);
      ring.lookAt(0, 0, 0);
      pinGroup.add(ring);

      // Pulse Ring (radar effect)
      const pulseMat = new THREE.MeshBasicMaterial({
        color: city.color,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
      });
      const pulseRing = new THREE.Mesh(pulseGeo, pulseMat);
      pulseRing.position.copy(pos);
      pulseRing.lookAt(0, 0, 0);
      pinGroup.add(pulseRing);

      pulsingRings.push({
        mesh: pulseRing,
        speed: 0.012 + Math.random() * 0.006,
        progress: Math.random(),
      });

      globeGroup.add(pinGroup);
    });

    // D. Curved Trade Routes and Vehicles
    const routesList = [];

    const createRoute = (
      start,
      end,
      height,
      color,
      vehicleMesh
    ) => {
      // 1. Bezier Curve
      const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
      const dist = start.distanceTo(end);
      mid.normalize().multiplyScalar(radius + height + dist * 0.18);
      const curve = new THREE.QuadraticBezierCurve3(start, end, mid);

      // 2. Main Line Geometry
      const points = curve.getPoints(60);
      const lineGeo = new THREE.BufferGeometry().setFromPoints(points);
      const lineMat = new THREE.LineBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.35, // subtle guide line
      });
      const line = new THREE.Line(lineGeo, lineMat);
      globeGroup.add(line);

      // 3. Animated particles along path
      const particles = [];
      const particleCount = 3;
      for (let i = 0; i < particleCount; i++) {
        const pMat = new THREE.MeshBasicMaterial({
          color: color,
          transparent: true,
          opacity: 0,
        });
        const pMesh = new THREE.Mesh(pGeo, pMat);
        globeGroup.add(pMesh);
        particles.push(pMesh);
      }

      // 4. Attach Vehicle
      globeGroup.add(vehicleMesh);

      routesList.push({
        line,
        curve,
        vehicle: vehicleMesh,
        vehicleSpeed: 0.0018 + Math.random() * 0.002,
        vehicleProgress: Math.random(),
        particles,
      });
    };

    // Add specific logistics routes with elevated heights to completely avoid surface clipping
    // NY -> London (Air Cargo Cyan)
    createRoute(cityNodes[0], cityNodes[1], 0.85, 0x00f2fe, createAirplaneMesh());
    // London -> Dubai (Air Cargo Purple)
    createRoute(cityNodes[1], cityNodes[2], 1.0, 0xa855f7, createAirplaneMesh());
    // Shanghai -> Dubai (Ocean Cargo Orange)
    createRoute(cityNodes[4], cityNodes[2], 0.24, 0xf97316, createShipMesh());
    // Dubai -> Singapore (Land Cargo Amber)
    createRoute(cityNodes[2], cityNodes[3], 0.42, 0xf59e0b, createTruckMesh());

    // E. Equatorial Hyperloop / Rail Route
    const railPoints = [];
    const tiltX = Math.PI / 7;
    const tiltZ = -Math.PI / 10;
    const railRadius = radius + 0.18; // Increased elevation to avoid mountain clipping
    for (let i = 0; i <= 80; i++) {
      const angle = (i / 80) * Math.PI * 2;
      const p = new THREE.Vector3(
        railRadius * Math.cos(angle),
        0,
        railRadius * Math.sin(angle)
      );
      p.applyAxisAngle(new THREE.Vector3(1, 0, 0), tiltX);
      p.applyAxisAngle(new THREE.Vector3(0, 0, 1), tiltZ);
      railPoints.push(p);
    }
    const railCurve = new THREE.CatmullRomCurve3(railPoints);
    const railPointsSample = railCurve.getPoints(100);
    const railGeo = new THREE.BufferGeometry().setFromPoints(railPointsSample);
    const railMat = new THREE.LineBasicMaterial({
      color: 0xfbbf24,
      transparent: true,
      opacity: 0.35, // subtle rail guide
    });
    const railLine = new THREE.Line(railGeo, railMat);
    globeGroup.add(railLine);

    // Rail particles
    const railParticles = [];
    const railParticleCount = 5;
    for (let i = 0; i < railParticleCount; i++) {
      const rMat = new THREE.MeshBasicMaterial({
        color: 0xfbbf24,
        transparent: true,
        opacity: 0,
      });
      const rMesh = new THREE.Mesh(pGeo, rMat);
      globeGroup.add(rMesh);
      railParticles.push(rMesh);
    }

    // Train
    const trainMesh = createTrainMesh();
    globeGroup.add(trainMesh);
    routesList.push({
      line: railLine,
      curve: railCurve,
      vehicle: trainMesh,
      vehicleSpeed: 0.0025,
      vehicleProgress: 0,
      particles: railParticles,
      isLoop: true,
    });

    // F. Space Dust Particles
    const dustCount = 180;
    const dustPositions = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount; i++) {
      dustPositions[i * 3] = (Math.random() - 0.5) * 16;
      dustPositions[i * 3 + 1] = (Math.random() - 0.5) * 16;
      dustPositions[i * 3 + 2] = (Math.random() - 0.5) * 16;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPositions, 3));
    const dustMat = new THREE.PointsMaterial({
      color: 0xfbbf24,
      size: 0.03,
      transparent: true,
      opacity: 0.35,
    });
    const spaceDust = new THREE.Points(dustGeo, dustMat);
    scene.add(spaceDust);

    // 7. Mouse movement parallax hook
    let targetRotationX = 0;
    let targetRotationY = 0;
    const handleMouseMove = (e) => {
      const x = (e.clientX - window.innerWidth / 2) / (window.innerWidth / 2);
      const y = (e.clientY - window.innerHeight / 2) / (window.innerHeight / 2);
      targetRotationY = x * 0.2;
      targetRotationX = y * 0.2;
    };
    window.addEventListener("mousemove", handleMouseMove);

    // 8. Resize Handler
    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      const w = r.width || width;
      const h = r.height || height;
      sceneFit = getSceneFit(w, h);

      camera.aspect = w / h;
      camera.position.z = sceneFit.cameraZ;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    window.addEventListener("resize", handleResize);

    // 9. Animation Loop
    let animFrameId;
    const clock = new THREE.Clock();
    const animate = () => {
      animFrameId = requestAnimationFrame(animate);
      const elapsedTime = clock.getElapsedTime();

      // Rotate globe group
      globeGroup.position.x += (targetRotationY * 0.3 - globeGroup.position.x) * 0.05;
      globeGroup.position.y += (-targetRotationX * 0.3 - globeGroup.position.y) * 0.05;

      // Animate pulsing city rings
      pulsingRings.forEach((p) => {
        p.progress += p.speed;
        if (p.progress >= 1.0) {
          p.progress = 0;
        }
        const scale = 1.0 + p.progress * 1.8;
        p.mesh.scale.set(scale, scale, 1);
        if (p.mesh.material instanceof THREE.MeshBasicMaterial) {
          p.mesh.material.opacity = 0.8 * (1.0 - p.progress);
        }
      });

      // Animate vehicle movements along their curves
      routesList.forEach((r) => {
        if (!r.vehicle) return;
        r.vehicleProgress += r.vehicleSpeed;
        if (r.vehicleProgress >= 1.0) {
          r.vehicleProgress = 0;
        }

        const pos = r.curve.getPointAt(r.vehicleProgress);
        // Look slightly ahead to align tangent rotation
        const tangent = r.curve.getTangentAt(r.vehicleProgress).normalize();
        const nextPos = pos.clone().add(tangent);

        r.vehicle.position.copy(pos);
        r.vehicle.up.copy(pos).normalize(); // Face outer normal
        r.vehicle.lookAt(nextPos);

        // Scale/fade vehicle as it approaches start/end points
        if (!r.isLoop) {
          const fadeLimit = 0.15;
          let scaleFactor = 1.0;
          if (r.vehicleProgress < fadeLimit) {
            scaleFactor = r.vehicleProgress / fadeLimit;
          } else if (r.vehicleProgress > 1.0 - fadeLimit) {
            scaleFactor = (1.0 - r.vehicleProgress) / fadeLimit;
          }
          r.vehicle.scale.set(scaleFactor, scaleFactor, scaleFactor);
        }
      });

      // Animate flowing particles along curves
      routesList.forEach((r) => {
        if (r.particles) {
          r.particles.forEach((pMesh, idx) => {
            const t = (elapsedTime * 0.25 + idx / r.particles.length) % 1.0;
            const pos = r.curve.getPointAt(t);
            pMesh.position.copy(pos);
            if (pMesh.material instanceof THREE.MeshBasicMaterial) {
              pMesh.material.opacity = Math.sin(t * Math.PI) * 0.8;
            }
          });
        }
      });

      // Slowly rotate background dust
      spaceDust.rotation.y += 0.0004;

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // 10. Cleanup
    return () => {
      cancelAnimationFrame(animFrameId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }

      // Geometries & Materials cleanups
      innerSphereGeo.dispose();
      innerSphereMat.dispose();
      landGeo.dispose();
      landMat.dispose();
      oceanGeo.dispose();
      oceanMat.dispose();
      dustGeo.dispose();
      dustMat.dispose();
      railGeo.dispose();
      railMat.dispose();

      // Cleanup custom geometries
      pulseGeo.dispose();
      pGeo.dispose();

      // Clean pulsing rings materials
      pulsingRings.forEach((p) => {
        if (p.mesh.material instanceof THREE.Material) {
          p.mesh.material.dispose();
        }
      });

      routesList.forEach((r) => {
        r.line.geometry.dispose();
        r.line.material.dispose();

        if (r.particles) {
          r.particles.forEach((pMesh) => {
            if (pMesh.material instanceof THREE.Material) {
              pMesh.material.dispose();
            }
          });
        }

        if (r.vehicle) {
          r.vehicle.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.geometry.dispose();
              if (Array.isArray(child.material)) {
                child.material.forEach((m) => m.dispose());
              } else {
                child.material.dispose();
              }
            }
          });
        }
      });

      renderer.dispose();
      controls.dispose();
    };
  }, [theme, orangeColor, amberColor, goldColor]);

  return (
    <div className="w-full h-full relative group/globe select-none">
      {/* CSS float animations injected locally */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-8px) rotate(0.5deg); }
        }
        @keyframes floatMedium {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(-1deg); }
        }
        @keyframes floatFast {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-6px) rotate(1deg); }
        }
        .animate-float-slow {
          animation: floatSlow 6s ease-in-out infinite;
        }
        .animate-float-medium {
          animation: floatMedium 5s ease-in-out infinite;
        }
        .animate-float-fast {
          animation: floatFast 4s ease-in-out infinite;
        }
      `}} />

      {/* ThreeJS Container */}
      <div
        ref={containerRef}
        className="w-full h-full min-h-[400px] md:min-h-[500px] lg:min-h-[600px] relative overflow-hidden cursor-grab active:cursor-grabbing z-0"
      />

      {/* Floating HUD Widget - Top Left (Real-time Tracking) */}
      <div className="absolute top-4 left-4 lg:-left-16 pointer-events-none z-10 animate-float-slow hidden sm:block">
        <div className="glass-panel p-3 rounded-xl border border-brand-orange/15 bg-bg-secondary/30 backdrop-blur-md flex flex-col gap-1.5 shadow-[0_0_20px_rgba(0,180,216,0.05)] w-[160px]">
          <div className="flex items-center gap-1.5 border-b border-text-muted/10 pb-1">
            <span className="w-2 h-2 rounded-full bg-neon-cyan animate-ping" />
            <span className="text-[8px] font-bold text-text-primary tracking-wider uppercase">REAL-TIME TRACKING</span>
          </div>
          <div className="text-[13px] font-mono font-bold text-brand-amber leading-none mt-0.5">
            LN-7489 / AKT
          </div>
          <div className="text-[8px] font-mono text-text-secondary leading-normal mt-0.5">
            LAT: 51.5074° N <br />
            LON: 0.1278° W
          </div>
          {/* Miniature animated mini-chart using SVG */}
          <div className="h-6 w-full mt-1">
            <svg viewBox="0 0 100 30" className="w-full h-full text-neon-cyan stroke-current fill-none">
              <path
                d="M0,25 L15,10 L30,18 L45,5 L60,15 L75,2 L90,12 L100,5"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M0,25 L15,10 L30,18 L45,5 L60,15 L75,2 L90,12 L100,5 L100,30 L0,30 Z"
                className="fill-neon-cyan/5"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Floating HUD Widget - Top Right (AI Algorithms) */}
      <div className="absolute top-4 right-4 lg:-right-12 pointer-events-none z-10 animate-float-medium hidden sm:block">
        <div className="glass-panel p-3 rounded-xl border border-brand-orange/15 bg-bg-secondary/30 backdrop-blur-md flex flex-col gap-1.5 shadow-[0_0_20px_rgba(245,158,11,0.05)] w-[160px]">
          <div className="flex items-center justify-between border-b border-text-muted/10 pb-1">
            <span className="text-[8px] font-bold text-brand-gold tracking-wider uppercase">AI OPTIMIZATION</span>
            <span className="px-1 py-0.5 rounded bg-brand-gold/10 text-[6px] font-bold text-brand-gold">ACTIVE</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="text-xl font-bold text-text-primary leading-none">98.4%</div>
            <div className="flex flex-col">
              <span className="text-[7px] font-bold text-neon-green leading-none">EFFICIENCY</span>
              <span className="text-[6px] font-mono text-text-secondary leading-none mt-0.5">+4.2% today</span>
            </div>
          </div>
          <div className="text-[7.5px] text-text-secondary leading-relaxed mt-1">
            Auto-routing avoids Rotterdam port latency. Saved 3.2 hours.
          </div>
        </div>
      </div>

      {/* Floating HUD Widget - Bottom Left (Global Network) */}
      <div className="absolute bottom-4 left-4 lg:-left-20 pointer-events-none z-10 animate-float-fast hidden lg:block">
        <div className="glass-panel p-3 rounded-xl border border-brand-orange/15 bg-bg-secondary/30 backdrop-blur-md flex flex-col gap-1.5 w-[180px] shadow-sm">
          <div className="text-[7px] font-bold text-text-muted tracking-widest uppercase border-b border-text-muted/10 pb-1">ACTIVE GLOBAL CHANNELS</div>
          <div className="grid grid-cols-2 gap-2 mt-0.5">
            <div className="flex flex-col">
              <span className="text-[11px] font-mono font-bold text-brand-amber">142</span>
              <span className="text-[6px] text-text-secondary uppercase">Air Freight Lanes</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[11px] font-mono font-bold text-brand-gold">89</span>
              <span className="text-[6px] text-text-secondary uppercase">Ocean Vessels</span>
            </div>
          </div>
          <div className="w-full bg-text-muted/10 h-[3px] rounded-full overflow-hidden mt-1">
            <div className="bg-brand-orange h-full rounded-full animate-pulse" style={{ width: '78%' }} />
          </div>
          <div className="text-[6px] font-mono text-text-muted text-right">Throughput: 1.2M MT/d</div>
        </div>
      </div>

      {/* Floating HUD Widget - Bottom Right (Data Stream Log) */}
      <div className="absolute bottom-4 right-4 lg:-right-16 pointer-events-none z-10 animate-float-slow hidden lg:block">
        <div className="glass-panel p-3 rounded-xl border border-brand-orange/15 bg-bg-secondary/30 backdrop-blur-md flex flex-col gap-1.5 w-[180px] shadow-sm">
          <div className="text-[7px] font-bold text-text-muted tracking-widest uppercase border-b border-text-muted/10 pb-1">DATASTREAM DEPLOYMENT</div>
          <div className="font-mono text-[6px] text-brand-gold/80 flex flex-col gap-0.5 mt-0.5 leading-normal">
            <div>&gt; CONNECTING TO TOKYO_HUB... OK</div>
            <div>&gt; SYNCING ERP LEDGER... 100%</div>
            <div>&gt; OPTIMIZING FREIGHT BATCHES...</div>
            <div className="text-neon-cyan animate-pulse">&gt; LATENCY INDEX: 14ms (OPTIMAL)</div>
          </div>
        </div>
      </div>
    </div>
  );
}
